import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type Theme = 'light' | 'dark';
export type ThemeMode = 'auto' | Theme;

const THEME_MODE_KEY = 'app-theme-mode';
const LEGACY_THEME_KEY = 'app-theme';

/** Feature flag: set `--ggj-phase8-lock-light` CSS var on :root OR localStorage key to lock the UI to light mode. */
export function isPhase8LockLight(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (localStorage.getItem('--ggj-phase8-lock-light') === 'true') return true;
  } catch { /* ignore */ }
  const val = getComputedStyle(document.documentElement).getPropertyValue('--ggj-phase8-lock-light').trim();
  return val === 'true' || val === '1';
}

/** Local wall-clock hour — `Date#getHours()` uses the browser’s timezone (same as user’s system / region). */
export const THEME_AUTO_DAY_START_HOUR = 6;
export const THEME_AUTO_DAY_END_HOUR = 18;

export function getTimeBasedTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const hour = new Date().getHours();
  return hour >= THEME_AUTO_DAY_START_HOUR && hour < THEME_AUTO_DAY_END_HOUR ? 'light' : 'dark';
}

export function getIanaTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Local';
  } catch {
    return 'Local';
  }
}

function readInitial(): { mode: ThemeMode; theme: Theme } {
  if (typeof window === 'undefined') return { mode: 'auto', theme: 'dark' };
  const modeRaw = localStorage.getItem(THEME_MODE_KEY);
  let mode: ThemeMode;
  if (modeRaw === 'auto' || modeRaw === 'light' || modeRaw === 'dark') {
    mode = modeRaw;
  } else {
    const leg = localStorage.getItem(LEGACY_THEME_KEY);
    if (leg === 'light' || leg === 'dark') mode = leg;
    else mode = 'auto';
  }
  const theme = mode === 'auto' ? getTimeBasedTheme() : mode;
  return { mode, theme };
}

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  ianaTimeZone: string;
  /** True when --ggj-phase8-lock-light feature flag is active; theme toggle is disabled. */
  lockedToLight: boolean;
  setTheme: (theme: Theme) => void;
  setThemeMode: (mode: ThemeMode) => void;
  /** Cycles: automatic (local time) → light (pinned) → dark (pinned) → automatic. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const lockedToLight = isPhase8LockLight();
  const initial = readInitial();
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => lockedToLight ? 'light' : initial.mode);
  const [theme, setThemeState] = useState<Theme>(() => lockedToLight ? 'light' : initial.theme);

  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = theme === 'dark' ? '#19191A' : '#6d5fd4';
  }, [theme]);

  const persistMode = (mode: ThemeMode) => {
    try {
      localStorage.setItem(THEME_MODE_KEY, mode);
      if (mode === 'auto') localStorage.removeItem(LEGACY_THEME_KEY);
      else localStorage.setItem(LEGACY_THEME_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    const next = mode === 'auto' ? getTimeBasedTheme() : mode;
    setThemeState(next);
    persistMode(mode);
  };

  const setTheme = (t: Theme) => {
    setThemeModeState(t);
    setThemeState(t);
    persistMode(t);
  };

  const toggleTheme = () => {
    if (lockedToLight) return;
    if (themeMode === 'auto') {
      setThemeModeState('light');
      setThemeState('light');
      persistMode('light');
      return;
    }
    if (themeMode === 'light') {
      setThemeModeState('dark');
      setThemeState('dark');
      persistMode('dark');
      return;
    }
    const t = getTimeBasedTheme();
    setThemeModeState('auto');
    setThemeState(t);
    persistMode('auto');
  };

  useEffect(() => {
    if (themeMode !== 'auto') return;
    const tick = () => {
      const next = getTimeBasedTheme();
      setThemeState((prev) => (prev !== next ? next : prev));
    };
    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, [themeMode]);

  useEffect(() => {
    if (themeMode !== 'auto') return;
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      const next = getTimeBasedTheme();
      setThemeState(next);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [themeMode]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== THEME_MODE_KEY && e.key !== LEGACY_THEME_KEY) return;
      const { mode, theme: t } = readInitial();
      setThemeModeState(mode);
      setThemeState(t);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const ianaTimeZone = getIanaTimeZone();

  return (
    <ThemeContext.Provider
      value={{ theme, themeMode, ianaTimeZone, lockedToLight, setTheme, setThemeMode, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
